
import { useState, useEffect, createContext, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { authRateLimiter } from "@/lib/rateLimiter";
import { toast } from "sonner";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  handleLogin: (email: string, password: string) => Promise<{ error: any }>;
  handleRegister: (email: string, password: string) => Promise<{ error: any }>;
  handleLogout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  handleLogin: async () => ({ error: null }),
  handleRegister: async () => ({ error: null }),
  handleLogout: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Set up auth state change listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log("Auth state change:", event, currentSession?.user?.id);
        
        if (mounted) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          setIsLoading(false);
        }
      }
    );

    // Get the initial session
    const getInitialSession = async () => {
      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting initial session:", error);
        }
        
        if (mounted) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error in getInitialSession:", error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    getInitialSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleLogin = async (email: string, password: string) => {
    const rateLimitKey = `login_${email}`;
    
    if (!authRateLimiter.isAllowed(rateLimitKey)) {
      const resetTime = authRateLimiter.getResetTime(rateLimitKey);
      const remainingMinutes = resetTime ? Math.ceil((resetTime - Date.now()) / 60000) : 15;
      toast.error(`Too many login attempts. Please try again in ${remainingMinutes} minutes.`);
      return { error: { message: "Rate limit exceeded" } };
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Don't increment rate limit for valid credential errors that aren't brute force
        if (error.message === "Invalid login credentials") {
          // Still track but don't immediately block for credential errors
        }
      } else {
        // Reset rate limit on successful login
        authRateLimiter.reset(rateLimitKey);
      }

      return { error };
    } catch (error) {
      console.error("Login error:", error);
      return { error };
    }
  };

  const handleRegister = async (email: string, password: string, username?: string) => {
    const rateLimitKey = `register_${email}`;
    
    if (!authRateLimiter.isAllowed(rateLimitKey)) {
      const resetTime = authRateLimiter.getResetTime(rateLimitKey);
      const remainingMinutes = resetTime ? Math.ceil((resetTime - Date.now()) / 60000) : 15;
      toast.error(`Too many registration attempts. Please try again in ${remainingMinutes} minutes.`);
      return { error: { message: "Rate limit exceeded" } };
    }

    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            username: username
          }
        }
      });
      return { error };
    } catch (error) {
      console.error("Registration error:", error);
      return { error };
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        handleLogin,
        handleRegister,
        handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
